// Chapter 29 - Self-Management
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 29.

import type { ChapterModule } from "./types";

const chapter29Module: ChapterModule = {
  meta: {
    id: "29",
    number: 29,
    title: "Self-Management",
    subtitle: "Self-monitoring, self-evaluation, self-instruction",
    part: "Part 11 - Special Applications",
    estMinutes: 70,
    bacb: ["G-19", "G-20"],
  },
  curated: true,
  overview: [
    "Chapter 29 defines SELF-MANAGEMENT as the personal application of behavior change tactics to produce a desired change in behavior. It is the gateway to controlling behaviors that external change agents cannot reach. The chapter distinguishes 'self-control' (often used loosely to mean delay of gratification) from 'self-management,' and explains delay discounting (temporal discounting).",
    "You will learn antecedent-based self-management tactics (manipulating MOs, response prompts, ABC manipulation, removing materials, restricting stimulus conditions, dedicating an environment), self-monitoring (the most widely used self-management strategy), goal setting plus self-evaluation, faded matching, self-instruction, and habit reversal.",
  ],
  objectives: [
    { id: "G-19", text: "Use self-monitoring, self-evaluation, and self-instruction procedures." },
    { id: "G-20", text: "Apply self-management tactics to acquire new behavior and to break unwanted habits." },
  ],
  coreConcepts: [
    {
      title: "Self as behavior controller (Skinner's two responses)",
      body: [
        "Skinner (1953) conceptualized self-control as a TWO-RESPONSE phenomenon: the CONTROLLING RESPONSE affects variables in such a way as to change the probability of the CONTROLLED RESPONSE.",
      ],
    },
    {
      title: "Definition: self-management",
      body: [
        "Self-management is the personal application of behavior change tactics that produces a desired change in behavior. It is a RELATIVE concept: programs may involve a small degree of self-management or be totally conceived, designed, and implemented by the person.",
      ],
    },
    {
      title: "Why prefer 'self-management' over 'self-control'",
      body: [
        "(a) Self-control implies the ultimate cause lies within the person; behavior analysis locates causal factors in the environment. (b) Self-control can serve as an explanatory fiction (a separate self inside controlling external behavior). (c) Lay and behavior analytic literatures often use 'self-control' to mean 'delay of gratification' - responding for a delayed but larger reward.",
      ],
    },
    {
      title: "Delay discounting",
      body: [
        "Delay (or temporal) discounting describes the decreased value of a delayed reward. The longer the delay, the less the reward influences current behavior. Understanding discounting helps design self-management tactics that bridge the gap to delayed rewards.",
      ],
    },
    {
      title: "Why self-management matters",
      body: [
        "Self-management can influence behaviors not accessible to external change agents; external agents often miss important instances; promotes generalization and maintenance; a small repertoire of self-management skills can control many behaviors; people with diverse abilities can learn it; some people perform better under self-selected tasks/criteria; group environments benefit; provides meaningful practice; aligns with educational ultimate goals; benefits society; feels freeing; and feels good.",
      ],
    },
    {
      title: "Antecedent-based self-management tactics",
      body: [
        "Manipulating MOs to make desired behavior more (or undesired less) likely; providing response prompts; performing initial steps of a behavior chain to confront a later SD; removing materials required for an undesired behavior; limiting an undesired behavior to restricted stimulus conditions; dedicating a specific environment for a desired behavior.",
      ],
    },
    {
      title: "Self-monitoring",
      body: [
        "Self-monitoring is observing and responding to (usually by recording) the behavior one is trying to change. The most widely used and studied self-management strategy. Often combined with goal setting and self-evaluation (comparing performance with a predetermined goal). Children can be taught accurate self-recording via FADED MATCHING TECHNIQUE: initially reinforced for matching teacher/parent data, with the matching requirement faded over time.",
      ],
    },
    {
      title: "Consequence-based self-management tactics",
      body: [
        "Self-administered consequences (rewards or losses); eliminate 'bootleg' reinforcement (sources of reinforcement that undermine the program); if necessary, put someone else in control of delivering consequences; use the LEAST complicated and intrusive contingencies that will be effective.",
      ],
    },
    {
      title: "Self-instruction and habit reversal",
      body: [
        "SELF-INSTRUCTION: talking to oneself, often as a verbal mediator that affects the occurrence of other behaviors. HABIT REVERSAL: multi-component treatment package in which the client self-monitors an unwanted habit and interrupts the behavior chain early by engaging in a behavior INCOMPATIBLE with the problem behavior.",
      ],
    },
  ],
  glossary: [
    { term: "Self-management", definition: "Personal application of behavior change tactics that produces a desired change in one's own behavior.", sourceHint: "We define self-management as the personal application of behavior change tactics that produces a desired change in behavior." },
    { term: "Controlling response", definition: "The response that affects variables to change the probability of the controlled response (Skinner, 1953).", sourceHint: "Skinner (1953) conceptualized self-control as a two-response phenomenon: The controlling response affects variables in such a way as to change the probability of the other, the controlled response." },
    { term: "Controlled response", definition: "The behavior that the controlling response is meant to change.", sourceHint: "Skinner (1953) conceptualized self-control as a two-response phenomenon." },
    { term: "Delay discounting", definition: "Decreased value of a delayed reward as a function of the delay (also called temporal discounting).", sourceHint: "People discount the value of delayed rewards; the greater the delay, the less value or influence the reward has on current behavior. Behavior analysts call this phenomenon delay discounting (or temporal discounting)." },
    { term: "Self-monitoring", definition: "Procedure in which a person observes and records the behavior they are trying to change.", sourceHint: "Self-monitoring is a procedure whereby a person observes and responds to, usually by recording, the behavior he is trying to change." },
    { term: "Self-evaluation", definition: "Comparing one's performance with a predetermined goal or standard.", sourceHint: "A person using self-evaluation compares her performance with a predetermined goal or standard." },
    { term: "Faded matching technique", definition: "Self-monitoring training in which the child is initially rewarded for matching adult-recorded data and the matching requirement is faded over time.", sourceHint: "Children can be taught to self-monitor and self-record their behavior accurately by means of a faded matching technique." },
    { term: "Self-instruction", definition: "Talking to oneself as a verbal mediator that affects the occurrence of other behaviors.", sourceHint: "Self-instruction (talking to oneself) can function as a controlling response (verbal mediator) that affects the occurrence of other behaviors." },
    { term: "Habit reversal", definition: "Multi-component treatment package in which clients self-monitor an unwanted habit and interrupt the chain early by emitting an incompatible response.", sourceHint: "Habit reversal is a multiple-component treatment package in which clients are taught to self-monitor their unwanted habits and interrupt the behavior chain as early as possible by engaging in a behavior incompatible with the problem behavior." },
    { term: "Bootleg reinforcement", definition: "Reinforcement that occurs outside the self-management program and undermines its effects.", sourceHint: "Eliminate 'bootleg reinforcement.'" },
  ],
  procedures: [
    {
      title: "Build a self-monitoring program",
      intro: "The most-evidenced self-management strategy.",
      steps: [
        { title: "Pick the target", detail: "Specify the controlled response; ensure the person can observe it." },
        { title: "Pick a recording method", detail: "Tally, checklist, wearable, app - any method the user will actually use." },
        { title: "Train recording accuracy", detail: "Use faded matching technique with an adult initially; fade the matching requirement." },
        { title: "Add goal setting + self-evaluation", detail: "Set a predetermined criterion; compare performance to criterion." },
        { title: "Layer reinforcement", detail: "Use self-delivered or externally delivered reinforcement for meeting goals." },
      ],
    },
    {
      title: "Use antecedent-based self-management",
      intro: "Make desired behavior more likely or undesired less.",
      steps: [
        { title: "Manipulate MOs", detail: "Use deprivation, satiation, or restriction to set up the desired behavior." },
        { title: "Use response prompts", detail: "Reminders, alarms, schedules; fade later." },
        { title: "Restructure stimulus conditions", detail: "Limit the undesired behavior to a specific time/place; dedicate a space for the desired behavior." },
        { title: "Remove materials", detail: "Take away cues or instruments for the undesired behavior." },
        { title: "Set up future SDs", detail: "Perform initial steps of a chain to ensure a later SD will evoke the desired behavior." },
      ],
    },
    {
      title: "Run a habit-reversal program",
      intro: "Detect early; interrupt with an incompatible response.",
      steps: [
        { title: "Self-monitor the habit", detail: "Identify cues and the earliest movements in the response chain." },
        { title: "Train competing response", detail: "Pick a behavior physically incompatible with the habit." },
        { title: "Implement contingent reversal", detail: "Engage the competing response at the earliest detection of the habit chain." },
        { title: "Reinforce and revise", detail: "Self-reinforce successful reversals; track data; revise the competing response if needed." },
      ],
    },
  ],
  examples: [
    { scenario: "A college student uses a phone app to record every cup of water they drink and reviews the total each evening.",
      application: "Self-monitoring with self-evaluation. Often results in increased target behavior even without explicit reinforcement.",
      sourceHint: "Self-monitoring evolved into the most widely used and studied self-management strategy because it often results in desired behavior change." },
    { scenario: "A learner sets out a packed gym bag in the entryway the night before to make morning workouts more likely.",
      application: "Antecedent-based self-management: performing the initial steps of the chain to ensure a future SD evokes the desired behavior.",
      sourceHint: "Performing the initial steps of a behavior chain to ensure being confronted later with a discriminative stimulus that will evoke the desired behavior." },
    { scenario: "A nail-biter learns to clench fists for one minute whenever they detect the early movements of bringing fingers to mouth.",
      application: "Habit reversal: incompatible competing response interrupts the chain.",
      sourceHint: "Habit reversal is a multiple-component treatment package in which clients are taught to self-monitor their unwanted habits and interrupt the behavior chain as early as possible by engaging in a behavior incompatible with the problem behavior." },
    { scenario: "Student saying 'first I will read the question, then underline the key words, then choose an answer.'",
      application: "Self-instruction as verbal mediator that increases the probability of correct responding.",
      sourceHint: "Self-instruction (talking to oneself) can function as a controlling response (verbal mediator) that affects the occurrence of other behaviors." },
  ],
  comparisons: [
    {
      title: "Self-control vs Self-management",
      left:  { label: "Self-control", points: [
        "Implies internal cause.",
        "Loose lay meaning: delay of gratification.",
        "Risk of explanatory fiction.",
      ]},
      right: { label: "Self-management", points: [
        "Located in environmental contingencies the person arranges.",
        "Behavior analytic and falsifiable.",
        "Two-response phenomenon (controlling + controlled).",
      ]},
    },
    {
      title: "Antecedent-based vs Consequence-based self-management",
      left:  { label: "Antecedent-based", points: [
        "Manipulate MOs, prompts, SDs, materials.",
        "Make desired behavior more likely BEFORE it occurs.",
      ]},
      right: { label: "Consequence-based", points: [
        "Self-administer reinforcers/losses.",
        "Eliminate bootleg reinforcement.",
        "Use least intrusive contingencies that work.",
      ]},
    },
    {
      title: "Self-monitoring vs Self-evaluation vs Self-instruction",
      left:  { label: "Self-monitoring / Self-evaluation", points: [
        "Self-monitoring: observe and record.",
        "Self-evaluation: compare performance to predetermined goal.",
      ]},
      right: { label: "Self-instruction", points: [
        "Talking to oneself as a verbal mediator.",
        "Used to evoke the next response in a chain.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Self-control is the will of an inner self.",
      reality: "Self-management is environmentally located: the person arranges variables (the controlling response) that change the probability of the controlled response.",
      sourceHint: "Self-control implies that the ultimate control of behavior lies within the person, but the causal factors for 'self-control' are to be found in a person's experiences with his environment.",
    },
    {
      myth: "Self-monitoring works only if accurate.",
      reality: "Self-monitoring often produces desired behavior change EVEN when accuracy is imperfect. Accuracy improves outcomes but is not strictly required for an effect.",
      sourceHint: "Self-monitoring evolved into the most widely used and studied self-management strategy because it often results in desired behavior change.",
    },
    {
      myth: "Self-instruction is private and unobservable.",
      reality: "Self-instruction is a verbal behavior with public traces (often spoken aloud during training) and serves as a controlling response that evokes other behaviors.",
      sourceHint: "Self-instruction (talking to oneself) can function as a controlling response (verbal mediator) that affects the occurrence of other behaviors.",
    },
    {
      myth: "Self-management is only for high-functioning adults.",
      reality: "People with diverse abilities can learn self-management. Faded matching trains accurate self-recording in children; antecedent tactics work across populations.",
      sourceHint: "People with diverse abilities can learn self-management skills.",
    },
  ],
  examChecklist: [
    "Define self-management and distinguish it from self-control.",
    "Describe Skinner's two-response analysis (controlling and controlled).",
    "Define delay discounting.",
    "List six antecedent-based self-management tactics.",
    "Define self-monitoring and self-evaluation.",
    "Describe the faded matching technique.",
    "Define self-instruction and habit reversal.",
    "List four consequence-based self-management guidelines.",
    "Explain bootleg reinforcement and how to eliminate it.",
  ],
  flashcardTerms: [
    "Self-management", "Controlling response", "Controlled response",
    "Delay discounting", "Self-monitoring", "Self-evaluation", "Faded matching technique",
    "Self-instruction", "Habit reversal", "Bootleg reinforcement",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Skinner conceptualized self-control as which kind of phenomenon?",
      options: [
        { label: "A", text: "A single-response phenomenon" },
        { label: "B", text: "A two-response phenomenon: controlling and controlled" },
        { label: "C", text: "An unconditioned response" },
        { label: "D", text: "A mentalistic construct" },
      ],
      answer: "B",
      explanation: "The controlling response arranges variables that change the probability of the controlled response.",
      topic: "Skinner's two-response analysis",
      sourceHint: "Skinner (1953) conceptualized self-control as a two-response phenomenon: The controlling response affects variables in such a way as to change the probability of the other, the controlled response.",
    },
    {
      id: "Q2",
      question: "Delay discounting describes:",
      options: [
        { label: "A", text: "The increased value of delayed rewards" },
        { label: "B", text: "The decreased value of delayed rewards" },
        { label: "C", text: "The MO for a reinforcer" },
        { label: "D", text: "An SD for self-monitoring" },
      ],
      answer: "B",
      explanation: "The longer the delay, the less value/influence a reward has on current behavior.",
      topic: "Delay discounting",
      sourceHint: "People discount the value of delayed rewards; the greater the delay, the less value or influence the reward has on current behavior.",
    },
    {
      id: "Q3",
      question: "Which is the MOST WIDELY USED self-management strategy?",
      options: [
        { label: "A", text: "Habit reversal" },
        { label: "B", text: "Self-instruction" },
        { label: "C", text: "Self-monitoring" },
        { label: "D", text: "Bootleg reinforcement" },
      ],
      answer: "C",
      explanation: "Self-monitoring is the most widely used and studied self-management strategy because it often produces desired behavior change.",
      topic: "Self-monitoring",
      sourceHint: "Self-monitoring evolved into the most widely used and studied self-management strategy because it often results in desired behavior change.",
    },
    {
      id: "Q4",
      question: "Habit reversal interrupts the chain by:",
      options: [
        { label: "A", text: "Removing reinforcement after the response" },
        { label: "B", text: "Engaging in a behavior INCOMPATIBLE with the problem behavior at the earliest detection" },
        { label: "C", text: "Punishing the response" },
        { label: "D", text: "Increasing the MO for the response" },
      ],
      answer: "B",
      explanation: "Habit reversal trains a competing response that is incompatible with the problem behavior and inserts it at the earliest detection of the chain.",
      topic: "Habit reversal",
      sourceHint: "Habit reversal is a multiple-component treatment package in which clients are taught to self-monitor their unwanted habits and interrupt the behavior chain as early as possible by engaging in a behavior incompatible with the problem behavior.",
    },
    {
      id: "Q5",
      question: "Which of the following is a CONSEQUENCE-based self-management guideline?",
      options: [
        { label: "A", text: "Manipulate the MO" },
        { label: "B", text: "Remove materials for the undesired behavior" },
        { label: "C", text: "Eliminate bootleg reinforcement" },
        { label: "D", text: "Dedicate a specific environment for the desired behavior" },
      ],
      answer: "C",
      explanation: "Bootleg reinforcement undermines self-management programs; eliminating it is a key consequence-based guideline.",
      topic: "Consequence-based tactics",
      sourceHint: "Eliminate 'bootleg reinforcement.'",
    },
  ],
  reviewPrompts: [
    "Distinguish self-control from self-management with an example of each.",
    "Design a self-monitoring program for a behavior you want to change.",
    "Plan a habit-reversal program for an unwanted habit.",
    "Apply six antecedent-based self-management tactics to a sample goal.",
    "Identify one source of bootleg reinforcement in a self-management plan and propose a fix.",
  ],
  recap: [
    "Self-management is the personal application of behavior change tactics. Self-control implies an inner cause; self-management locates causes in the environment. Skinner's two-response analysis splits the controlling and controlled responses.",
    "Delay discounting explains why delayed rewards lose influence. Antecedent-based tactics (MOs, prompts, restricted SDs, dedicated environments, chain pre-emption, material removal) make desired behavior more likely. Self-monitoring is the most widely used strategy; goal setting + self-evaluation amplifies it.",
    "Consequence-based tactics include self-administered rewards/losses, eliminating bootleg reinforcement, and using the least intrusive contingencies that work. Self-instruction acts as a verbal mediator; habit reversal interrupts unwanted chains with an incompatible response.",
  ],
  sourceExcerpts: [
    {
      heading: "Skinner's two-response analysis",
      text: "Skinner (1953) conceptualized self-control as a two-response phenomenon: The controlling response affects variables in such a way as to change the probability of the other, the controlled response.",
    },
    {
      heading: "Self-management defined",
      text: "We define self-management as the personal application of behavior change tactics that produces a desired change in behavior. Self-management is a relative concept. A behavior change program may entail a small degree of self-management or be totally conceived, designed, and implemented by the person.",
    },
    {
      heading: "Self-monitoring",
      text: "Self-monitoring is a procedure whereby a person observes and responds to, usually by recording, the behavior he is trying to change. Self-monitoring evolved into the most widely used and studied self-management strategy because it often results in desired behavior change.",
    },
    {
      heading: "Habit reversal",
      text: "Habit reversal is a multiple-component treatment package in which clients are taught to self-monitor their unwanted habits and interrupt the behavior chain as early as possible by engaging in a behavior incompatible with the problem behavior.",
    },
  ],
};

export default chapter29Module;
